import { capitalizeEachWord } from "../../../../utils/capitalize";
import { formatInterval } from "../../../../utils/interval";
import { formatDatetime } from "../../../../utils/iso8601";
import { notEmpty } from "../../../../utils/not-empty";
import { channelRecipientName } from "../../../moment-card/components/moment-card-chip";
import { classifyMomentWhen } from "../../../moment-card/components/moment-card-when";
import { segmentDisplayName } from "../../../moment/hooks/use-moment-recipients-classify";
import { getToTarget } from "../../../to/to-target";

import type { FeedItem } from "../../hooks/use-recently-edited";
import type { UpcomingSend } from "../../hooks/use-upcoming-sends";
import type { ReactNode } from "react";

/** A Communication as either feed has it. */
export type FeedCommunication = FeedItem | UpcomingSend;

interface Audience {
  /** Who it goes to, for picking an icon. */
  kind: "segment" | "connection" | "notify" | "channel" | "none";
  label: string;
}

const NO_AUDIENCE: Audience = { kind: "none", label: "Recipients not defined" };

/** "Jessica", or "Jessica and 2 others". */
const listed = ([first, ...rest]: string[]): string =>
  rest.length === 0
    ? first!
    : `${first} and ${rest.length} ${rest.length === 1 ? "other" : "others"}`;

const plural = (word: string): string => {
  if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
  if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
  return `${word}s`;
};

/** A connection's key as people it names: buddy is Buddies, line_manager Line Managers. */
const connectionPlural = (key: string): string =>
  plural(capitalizeEachWord(key.replaceAll("_", " ")));

/** Who a Communication in the feed goes to, as one line. */
export const communicationAudience = (row: FeedCommunication): Audience => {
  const target = getToTarget({
    target_channel: row.target_channel ?? false,
    target_manager: row.target_manager ?? false,
    only_additional_recipients: row.only_additional_recipients ?? false,
    notify_channel: row.notify_channel ?? false,
    target_connection_type: row.target_connection_type,
  });
  const segment = row.segment && segmentDisplayName(row.segment.operation, row.segment.name);
  const recipients = (row.additional_recipients_json ?? []).filter(notEmpty);

  switch (target.toTarget) {
    case "channel": {
      // Without the # ChannelChip puts before them; the row's icon stands for it.
      const channels = recipients.map(channelRecipientName);
      if (channels.length === 0) return NO_AUDIENCE;
      const label = listed(channels);
      return {
        kind: "channel",
        label: target.notify_channel && segment ? `${label} about ${segment}` : label,
      };
    }
    case "notify": {
      const names = recipients.map(({ display, destination }) => display ?? destination);
      if (names.length === 0) return NO_AUDIENCE;
      const label = listed(names);
      return { kind: "notify", label: segment ? `${label} about ${segment}` : label };
    }
    case "manager":
      return segment ? { kind: "connection", label: `Managers of ${segment}` } : NO_AUDIENCE;
    case "connection":
      return segment
        ? {
            kind: "connection",
            label: `${connectionPlural(target.target_connection_type)} of ${segment}`,
          }
        : NO_AUDIENCE;
    case "employee":
    default:
      return segment ? { kind: "segment", label: segment } : NO_AUDIENCE;
  }
};

interface Timing {
  /** How it's sent, for picking an icon. */
  kind: "immediate" | "scheduled" | "triggered" | "survey-completed" | "none";
  content: ReactNode;
}

/** A send at a set time, in its own timezone or each recipient's. */
const scheduledAt = (when: string, timezone: string | null): string =>
  `${formatDatetime(new Date(when))} (${timezone ?? "Recipients’ local timezone"})`;

/**
 * When a Communication in the feed is sent, as one line. With `nextSendAt`, when it next sends
 * instead, as Upcoming sends has it.
 */
export const communicationTiming = (row: FeedCommunication, nextSendAt?: string): Timing => {
  if (nextSendAt) return { kind: "scheduled", content: scheduledAt(nextSendAt, row.timezone) };

  // Without its follow-up, which only names what it's sent after: the feed has just that title.
  const { follow_up: followUp, ...timing } = row;
  const when = classifyMomentWhen(timing);

  switch (when.type) {
    case "immediate": {
      if (!when.publishedAt) return { kind: "immediate", content: "Sends when published" };
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return {
        kind: "immediate",
        content: `${formatDatetime(new Date(when.publishedAt))} (${userTimeZone})`,
      };
    }
    case "scheduled":
      return { kind: "scheduled", content: scheduledAt(when.when, when.timezone) };
    case "triggered":
      return {
        kind: "triggered",
        content: (
          <>
            {formatInterval(when.interval)}{" "}
            <strong>
              {followUp ? followUp.config.moment_title : (row.event ?? when.eventKey)}
            </strong>
          </>
        ),
      };
    case "survey-completed":
      return { kind: "survey-completed", content: "Survey triggered" };
    case "unscheduled":
    default:
      return { kind: "none", content: "Schedule not defined" };
  }
};
